import { ContractualMonth, FosEndLookup, MonthLookup, OffsetLookup, MonthShortLookup } from "@shared/models";
import { CONTRACTUAL_MONTHS } from "@shared/misc";

/**
 * @description Gets the contractual month object based on the month number.
 * @param {MonthLookup} lookup - Wrapper {@link MonthLookup} containing month as a number.
 * @returns {ContractualMonth} - {@link ContractualMonth} object.
 */
export function getContractualMonth(lookup: MonthLookup): ContractualMonth; // Overload for direct month lookup

/**
 * @description Gets the contractual month object based on the current month and an offset.
 * @param {OffsetLookup} lookup - Wrapper {@link OffsetLookup} containing number of months to offset from the current month.
 * @returns {ContractualMonth} - {@link ContractualMonth} object.
 */
export function getContractualMonth(lookup: OffsetLookup): ContractualMonth; // Overload for offset lookup

/**
 * @description Gets the contractual month object based on the FOS end month.
 * @param {FosEndLookup} lookup - Wrapper {@link FosEndLookup} containing FOS end month.
 * @returns {ContractualMonth} - {@link ContractualMonth} object.
 */
export function getContractualMonth(fosEnd: FosEndLookup): ContractualMonth; // Overload for FOS end lookup

export function getContractualMonth(monthShort: MonthShortLookup): ContractualMonth; // Overload for month short lookup

export function getContractualMonth(
  lookup: MonthLookup | OffsetLookup | FosEndLookup | MonthShortLookup
): ContractualMonth {
  const today = new Date();
  const year = today.getFullYear();

  if ("month" in lookup) {
    // Direct month lookup
    return CONTRACTUAL_MONTHS.find((contractualMonth) => parseInt(contractualMonth.asInt) === lookup.month)!;
  } else if ("offset" in lookup) {
    // Offset lookup
    const currentMonthIndex = CONTRACTUAL_MONTHS.findIndex((month) => {
      const start = new Date(
        `${year}-${month.start.month.padStart(2, "0")}-${month.start.day.padStart(2, "0")}T00:00:00`
      );
      const end = new Date(`${year}-${month.end.month.padStart(2, "0")}-${month.end.day.padStart(2, "0")}T23:59:59`);
      return today >= start && today <= end;
    });

    const desiredMonthIndex =
      (currentMonthIndex + lookup.offset + CONTRACTUAL_MONTHS.length) % CONTRACTUAL_MONTHS.length;
    return CONTRACTUAL_MONTHS[desiredMonthIndex];
  } else if ("fosEnd" in lookup) {
    // FOS Month End Lookup
    const fosEndMonth = lookup.fosEnd;
    // find the CONTRACTUAL_MONTHS that has start.fosEnd === fosEndMonth
    return CONTRACTUAL_MONTHS.find((contractualMonth) => contractualMonth.end.fosEnd === fosEndMonth)!;
  } else {
    // Month short lookup
    return CONTRACTUAL_MONTHS.find((contractualMonth) => contractualMonth.monthShort === lookup.monthShort)!;
  }
}

/***
 * getTwelveMonthsArray
 * @description returns an array of months starting with a month defined by the parameter startMonth.
 * @param {string} startMonth - The month to start the array with
 */

export function getTwelveMonthsArray(startMonth: string) {
  const months = [];
  const fullMonths = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const startIndex = fullMonths.indexOf(startMonth.toUpperCase());

  if (startIndex === -1) {
    throw new Error("Invalid start month");
  }

  for (let i = 0; i < 12; i++) {
    const monthIndex = (startIndex + i) % 12;
    months.push(fullMonths[monthIndex]);
  }

  return months;
}
